import { route } from "./routes.js";
import { z } from "zod";
import { ringSchema } from "@zion/sdk-contracts";
//#region src/minimum-ring.ts
/**
* `GET /v1/admin/minimum-ring` — the CP-projected floor on the maturity ring this org may consume.
*
* Producer: `AdminMinimumRingController.getMinimumRing` in `cloud/dp/core-api/src/admin/`.
* Consumer: the admin app's platform-status banner and its ring pickers, which disable
* out-of-range options. Requires `channels:read`. Read-only on the DP: the floor is owned by CP and
* projected into `release.tenant_minimum_ring`.
*
* ⛔ `MinimumRingPolicy` from `@zion/sdk-contracts` is NOT this body and is not imported here.
* That interface is `{ orgId, minimumRing, setBy, setAt, reason }` — the CP twin's shape. The DP
* sends `{ minimumRing, setByEmail, setAt, reason, syncedAt }`: `setByEmail` rather than `setBy`,
* plus `syncedAt`, and no `orgId`, because the org comes from the caller's own JWT and echoing it
* back proves nothing that was in doubt. The DP's key set is the existing contract and renaming it
* was ruled out.
*
* The route always answers 200 and the body carries the discriminant. It threw 404 for an uncapped
* org until #1466, which collapsed "no floor" and "could not ask" into one status. There is no 2xx
* body that means unknown: a guard refusal is 401 or 403 and a failure is 5xx.
*
* The shell's `MinimumRingPolicyResponse` omits `syncedAt` because nothing in the shell reads it.
* The producer sends it, so it is required on both arms here.
*
* No request schema: the route takes no body, no query and no parameter.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped rather than refused and one new column cannot red every page that reads the route.
* Every request schema is `z.strictObject`, so a key the server would reject is refused before
* the request leaves the client. No shape in this file travels in both directions.
*/
/**
* The 200 body when the org has a CP-projected floor.
*
* `minimumRing` is never null on this arm: `release.tenant_minimum_ring.minimum_ring` is `NOT NULL`
* with a CHECK constraint on the four ring names, so "a row exists but names no ring" is
* unrepresentable in the table and stays unrepresentable here. Absence is always no row.
*
* The four ring names come from `ringSchema` in `@zion/sdk-contracts`, the single ring vocabulary,
* imported rather than respelled (R-27). The producer declares the column as a bare `string`; the
* CHECK constraint is what makes the closed set true.
*
* `syncedAt` is when CP last pushed the policy, and is non-null on this arm.
*
* Owner: `MinimumRingPolicyProjection` in
* `cloud/dp/core-api/src/admin/dto/minimum-ring-policy-response.dto.ts`.
*/
const minimumRingPolicyProjectionSchema = z.object({
	minimumRing: ringSchema,
	setByEmail: z.string().nullable(),
	setAt: z.iso.datetime().nullable(),
	reason: z.string().nullable(),
	syncedAt: z.iso.datetime()
});
/**
* The 200 body for an org with no minimum-ring policy.
*
* Every field is the literal `null`, which is what makes {@link getMinimumRingPolicyResponseSchema}
* a discriminated union: narrowing on `minimumRing !== null` yields a full projection, and a
* consumer cannot read `setByEmail` as a string without first proving a policy exists.
*
* The key set is invariant across the two arms, deliberately. A `{}` body would make `minimumRing`
* absent rather than null, and a client reading `body.minimumRing` cannot tell an absent key from a
* key the server has not implemented yet — both read `undefined`. `null` is an assertion;
* `undefined` is a shrug. For the same reason the route is never 204 and never an empty body.
*
* `setByEmail`, `setAt` and `syncedAt` are null, never fabricated: with no row there is no such
* operator, no such instant and no such push.
*
* Owner: `NoMinimumRingPolicy` and `noMinimumRingPolicy()` in the same dto file.
*/
const noMinimumRingPolicySchema = z.object({
	/** `null` means no floor. Never `'stable'`, which would cap every uncapped org. */
	minimumRing: z.null(),
	setByEmail: z.null(),
	setAt: z.null(),
	reason: z.null(),
	syncedAt: z.null()
});
/** `GET /v1/admin/minimum-ring` — always 200. Requires `channels:read`. */
const GET_MINIMUM_RING_ROUTE = route("GET", "/v1/admin/minimum-ring");
/**
* The body of `GET /v1/admin/minimum-ring` — a union on `minimumRing`.
*
* The no-policy arm is tried first so an all-null body parses as
* {@link noMinimumRingPolicySchema} rather than failing the projection arm's ring check. The two
* arms are mutually exclusive, so the order changes no verdict.
*/
const getMinimumRingPolicyResponseSchema = z.union([noMinimumRingPolicySchema, minimumRingPolicyProjectionSchema]);
/** Every route this file names, for `route-census.ts`. */
const minimumRingRoutes = Object.freeze([GET_MINIMUM_RING_ROUTE]);
//#endregion
export { GET_MINIMUM_RING_ROUTE, getMinimumRingPolicyResponseSchema, minimumRingPolicyProjectionSchema, minimumRingRoutes, noMinimumRingPolicySchema };

//# sourceMappingURL=minimum-ring.js.map